import Link from "next/link";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

export function Container({ as: Tag = "div", size = "wide", className = "", children }) {
  const sizes = {
    wide: "max-w-[80rem]",
    reading: "max-w-3xl",
    form: "max-w-5xl",
  };

  return (
    <Tag className={`mx-auto w-full px-5 sm:px-7 lg:px-10 ${sizes[size] || sizes.wide} ${className}`}>
      {children}
    </Tag>
  );
}

export function Section({ as: Tag = "section", tone = "canvas", className = "", children, ...props }) {
  const tones = {
    canvas: "bg-water-canvas",
    surface: "bg-water-surface",
    sand: "bg-water-sand",
    ink: "bg-water-ink text-white",
  };

  return (
    <Tag className={`${tones[tone] || tones.canvas} py-16 sm:py-20 lg:py-24 ${className}`} {...props}>
      {children}
    </Tag>
  );
}

export function Eyebrow({ children, className = "" }) {
  if (!children) return null;
  return (
    <p className={`text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue ${className}`}>
      {children}
    </p>
  );
}

export function SectionHeader({ eyebrow, title, description, align = "left", className = "" }) {
  const alignment = align === "center" ? "mx-auto text-center" : "";
  return (
    <div className={`max-w-3xl ${alignment} ${className}`}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className={`${eyebrow ? "mt-3" : ""} text-balance text-3xl font-semibold tracking-[-0.025em] text-water-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.08]`}>
        {title}
      </h2>
      {description ? (
        <p className="mt-5 text-pretty text-lg leading-8 text-water-muted">{description}</p>
      ) : null}
    </div>
  );
}

export function ButtonLink({ href, children, variant = "primary", className = "", arrow = false, ...props }) {
  const variants = {
    primary:
      "border-water-blue bg-water-blue text-white hover:border-water-blue-dark hover:bg-water-blue-dark",
    secondary:
      "border-water-border bg-water-surface text-water-ink hover:border-water-blue hover:text-water-blue",
    dark: "border-water-ink bg-water-ink text-white hover:border-slate-700 hover:bg-slate-800",
    text: "border-transparent bg-transparent px-0 text-water-blue hover:text-water-blue-dark",
  };
  const classes = `inline-flex min-h-12 items-center justify-center gap-2 rounded-[0.875rem] border px-5 py-3 text-sm font-semibold transition-[background-color,border-color,color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 touch-manipulation ${variants[variant] || variants.primary} ${className}`;
  const content = (
    <>
      {children}
      {arrow ? <ArrowRightIcon className="h-4 w-4" aria-hidden="true" /> : null}
    </>
  );

  if (href?.startsWith("http")) {
    return <a href={href} className={classes} {...props}>{content}</a>;
  }

  return <Link href={href} className={classes} {...props}>{content}</Link>;
}

export function EditorialCard({ as: Tag = "article", interactive = false, className = "", children }) {
  return (
    <Tag className={`rounded-[1.375rem] border border-water-border bg-water-surface ${interactive ? "transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_16px_40px_rgba(11,34,57,0.08)]" : ""} ${className}`}>
      {children}
    </Tag>
  );
}

export const fieldClassName =
  "mt-2 block min-h-12 w-full rounded-[0.875rem] border border-water-border bg-white px-4 py-3 text-base text-water-ink shadow-none transition-[border-color,box-shadow] placeholder:text-slate-400 focus:border-water-blue focus:outline-none focus:ring-2 focus:ring-water-blue/20 disabled:cursor-not-allowed disabled:bg-slate-100";

export function FieldLabel({ htmlFor, children, optional = false }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-semibold text-water-ink">
      {children} {optional ? <span className="font-normal text-water-muted">(optional)</span> : <span className="text-red-700" aria-hidden="true">*</span>}
    </label>
  );
}

export function InlineError({ id, children }) {
  if (!children) return null;
  return <p id={id} className="mt-2 text-sm leading-5 text-red-700">{children}</p>;
}
